import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { SearchIcon } from '@icons';
import XIcon from '@icons/XIcon';
import { setupForDark } from '@theme';

interface SearchBarProps extends React.HTMLAttributes<HTMLDivElement> {
  onSearch: (value: string) => void;
  defaultValue?: string;
  className?: string;
  disabled?: boolean;
}
export default function SearchBar({
  onSearch,
  defaultValue = '',
  className = '',
  disabled = false,
  ...props
}: SearchBarProps) {
  const { t } = useTranslation();
  const [inputFocus, setInputFocus] = useState(defaultValue.length > 0);
  const [values, setValues] = useState({
    current: defaultValue,
    previous: ''
  });

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setInputFocus(true);
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      setInputFocus(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValues((prev) => {
      return {
        current: value,
        previous: prev.current
      };
    });
  };

  useEffect(() => {
    if ((values.current.length === 0 && values.previous.length > 0) || values.current.length > 0) {
      let timeoutId = setTimeout(() => onSearch(values.current), 500);
      return () => clearTimeout(timeoutId);
    }
  }, [values.current]);

  return (
    <div
      {...props}
      className={classNames(
        'flex rounded-xs pt-xs pb-xs pl-sm pr-sm',
        setupForDark('text-tertiary', 'bg-search', 'border-color-search-default'),
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
    >
      <SearchIcon
        size={24}
        className={classNames({
          'mr-xs': true,
          [setupForDark('text-icon-secondary')]: inputFocus === false,
          [setupForDark('text-icon-primary')]: inputFocus === true
        })}
      />
      <input
        // Prevent auto-focus on mobile sidebar open due to Radix UI Dialog behavior.
        // tabIndex={-1} removes input from tab order while preserving programmatic focus capability.
        tabIndex={-1}
        type='text'
        value={values.current}
        placeholder={
          disabled
            ? t('components.searchBar.disabledPlaceholder')
            : t('components.searchBar.placeholder')
        }
        disabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        className={classNames({
          'border-none p-0 m-0 bg-transparent outline-none w-full': true,
          [setupForDark('text-icon-secondary')]: inputFocus === false,
          [setupForDark('text-icon-primary')]: inputFocus === true
        })}
      />
      {values.current.length > 0 && (
        <XIcon
          size={24}
          className={classNames(setupForDark('text-icon-secondary'), 'cursor-pointer')}
          onClick={() => {
            setValues({ current: '', previous: '' });
            onSearch('');
          }}
        />
      )}
    </div>
  );
}
